import { useEffect, useState } from "react";
import Modal from "../ui/Modal";
import ThemeToggle from "../ui/ThemeToggle";
import "./ProfileButton.css";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ApiError, getMe, submitPostRoute, updateTimezone} from "../../api/Client";

interface ProfileButtonProps {
  onLogout?: () => void;
  className?: string;
}

/** "Ada Lovelace" -> "AL" */
function initialsFor(name: string | undefined): string {
  if (!name) return "";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

// The fixed IANA tz database, straight from the runtime — every modern
// browser and Node 18+ ship it, so this needs no data of its own and can
// never drift out of date.
const TIMEZONES = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];

// A 429 here specifically means "you changed this too recently" (see the
// cooldown on PATCH /me/timezone) — worth its own clear copy rather than
// whatever generic text a raw request-failed message would show, and
// independent of whether the backend bothers sending a friendly `message`.
function timezoneErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 429) {
    return typeof (error.body as any)?.message === "string"
        ? (error.body as any).message
        : "You've already changed your timezone recently — try again in a few days.";
  }
  if (error instanceof Error) return error.message;
  return "Couldn't update your timezone.";
}

export default function ProfileButton({ onLogout, className = "" }: ProfileButtonProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
  });

  // /me always resolves 200 — {loggedIn: false} is a real, valid response,
  // not a loading state — so this (not query-loading status) is the actual
  // signal for whether `data` has a real user in it yet.
  const isLoggedIn = !!data?.loggedIn;

  // Local selection, seeded from the server value once it's loaded and kept
  // in sync if it changes out from under us (e.g. a fresh /me refetch).
  const [selectedTimezone, setSelectedTimezone] = useState<string | null>(null);
  useEffect(() => {
    if (data?.timezone) setSelectedTimezone(data.timezone);
  }, [data?.timezone]);

  const timezoneMutation = useMutation({
    mutationFn: (timezone: string) => updateTimezone(timezone),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] }),
  });

  const hasTimezoneChange = selectedTimezone !== null && selectedTimezone !== data?.timezone;

  return (
    <>
      <button
        className={`profile-button ${className}`.trim()}
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open profile menu"
      >
        <span className="profile-button__avatar" aria-hidden="true">
          {isLoggedIn ? initialsFor(data.name) : ""}
        </span>
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Your account">
        <div className="profile-modal__row">
          <span className="profile-modal__avatar" aria-hidden="true">
            {isLoggedIn ? initialsFor(data.name) : ""}
          </span>
          <div>
            <p className="profile-modal__name">{isLoggedIn ? data.name : ""}</p>
            <p className="profile-modal__email">{isLoggedIn ? data.email : ""}</p>
          </div>
        </div>

        <div className="profile-modal__theme">
          <span className="profile-modal__theme-label">Appearance</span>
          <ThemeToggle />
        </div>

        {isLoggedIn && (
            <div className="profile-modal__timezone">
              <label className="profile-modal__timezone-label" htmlFor="profile-timezone-select">
                Timezone
              </label>
              <p className="profile-modal__timezone-hint">
                Your daily free limit resets at midnight in this timezone.
              </p>
              <div className="profile-modal__timezone-row">
                <select
                    id="profile-timezone-select"
                    className="profile-modal__timezone-select"
                    value={selectedTimezone ?? ""}
                    onChange={(e) => {
                      setSelectedTimezone(e.target.value);
                      timezoneMutation.reset();
                    }}
                >
                  {selectedTimezone && !TIMEZONES.includes(selectedTimezone) && (
                      // Keeps whatever the server has selected even if it's
                      // somehow not in this runtime's list, instead of
                      // silently switching the <select> to a blank value.
                      <option value={selectedTimezone}>{selectedTimezone}</option>
                  )}
                  {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
                <button
                    className="profile-modal__timezone-save"
                    type="button"
                    disabled={!hasTimezoneChange || timezoneMutation.isPending}
                    onClick={() => selectedTimezone && timezoneMutation.mutate(selectedTimezone)}
                >
                  {timezoneMutation.isPending ? "Saving…" : "Save"}
                </button>
              </div>

              {timezoneMutation.isError && (
                  <p className="profile-modal__timezone-status profile-modal__timezone-status--error">
                    {timezoneErrorMessage(timezoneMutation.error)}
                  </p>
              )}
              {timezoneMutation.isSuccess && !hasTimezoneChange && (
                  <p className="profile-modal__timezone-status profile-modal__timezone-status--success">
                    Timezone updated.
                  </p>
              )}
            </div>
        )}

        <div className="profile-modal__actions">
          {isLoggedIn ? (
              <>
                {data.subscribed ?
                    <button className="profile-modal__action" type="button" onClick={() => submitPostRoute("/manage")}>
                      Manage Subscription
                    </button>
                    :
                    <button className="profile-modal__action profile-modal__action--upgrade" type="button" onClick={() => submitPostRoute("/checkout")}>
                      Upgrade
                    </button>
                }
                <button
                    className="profile-modal__action profile-modal__action--danger"
                    type="button"
                    onClick={onLogout}
                >
                  Log out
                </button>
              </>
          ) : (
              <a className="profile-modal__action profile-modal__action--upgrade" href="/login">
                Log in
              </a>
          )}
        </div>

        <p className="profile-modal__legal">
          <a href="/privacy.pdf" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          {" · "}
          <a href="/terms.pdf" target="_blank" rel="noopener noreferrer">Terms</a>
        </p>
      </Modal>
    </>
  );
}
